/**
 * PlayerAssetAddEditPage — V2 edit-page chrome for the player-asset catalogs
 * (avatars, player backgrounds, app music). Same sticky header as
 * BackgroundAddEditPageV2; the form + live preview live in AddEditPlayerAsset.
 */
import React, { useCallback, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { Button } from "../../redesign";
import AddEditPlayerAsset from "./AddEditPlayerAsset";

const SaveIconSvg = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

function PlayerAssetAddEditPage({ config }) {
  const location = useLocation();
  const { id } = useParams();
  const isEdit = location.pathname.indexOf("edit") > -1;
  const formRef = React.useRef();

  const [loaded, setLoaded] = useState(null);
  const handleLoaded = useCallback(asset => setLoaded(asset), []);

  const handleSave = e => {
    e.preventDefault();
    formRef.current?.submit();
  };

  const singular = config.singular.toLowerCase();
  const eyebrow = isEdit ? `Editing ${singular}` : `New ${singular}`;
  const title = isEdit ? loaded?.name || `${config.singular} #${id}` : `Create New ${config.singular}`;

  return (
    <div className="bg-bg min-h-full">
      <div className="sticky top-0 bg-bg border-b border-sand" style={{ zIndex: 20 }}>
        <div className="flex items-start" style={{ padding: "20px 28px", gap: 16, flexWrap: "wrap" }}>
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: "0.08em" }}
            >
              {eyebrow}
            </div>
            <h1
              className="text-navy truncate"
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: "4px 0 4px",
                letterSpacing: "-0.015em",
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              {isEdit ? (
                <>
                  id · <strong className="text-navy">{id}</strong>
                  {loaded?.slug && (
                    <>
                      {" · slug · "}
                      <strong className="text-navy">{loaded.slug}</strong>
                    </>
                  )}
                </>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to={config.basePath} className="no-underline">
              <Button variant="ghost">Cancel</Button>
            </Link>
            <Button variant="coral" onClick={handleSave} leadingIcon={SaveIconSvg}>
              {isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: "24px 32px 60px" }}>
        <AddEditPlayerAsset ref={formRef} isEdit={isEdit} selectedId={id} config={config} onLoaded={handleLoaded} />
      </div>
    </div>
  );
}

export default PlayerAssetAddEditPage;
